/**
 * Validates a URL string according to strict requirements:
 * - Must include http:// or https:// protocol
 * - No spaces allowed
 * - Must be a valid URL format
 * - Email addresses are not allowed
 *
 * @param url The URL string to validate
 * @returns boolean - true if valid, false otherwise
 */
export function checkValidHttps(url: string | undefined): boolean {
  // Check for null, undefined or empty string
  if (!url) {
    return false
  }

  // Check for spaces
  if (/\s/.test(url)) {
    return false
  }

  // Check if it looks like an email address
  if (/^[^@]+@[^@]+\.[^@]+$/.test(url)) {
    return false
  }

  try {
    // Create URL object to validate the URL format
    const urlObj = new URL(url)

    // Check if protocol is http or https
    if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
      return false
    }

    // Check if hostname exists and is valid
    if (!urlObj.hostname || urlObj.hostname.length < 1) {
      return false
    }

    // Check if hostname has at least one dot (for domain validity)
    if (!urlObj.hostname.includes(".")) {
      return false
    }

    // Additional check to ensure it's not an IP address
    const ipAddressRegex = /^(\d{1,3}\.){3}\d{1,3}$/
    if (ipAddressRegex.test(urlObj.hostname)) {
      // If it's an IP address, make sure it's valid
      const octets = urlObj.hostname.split(".")
      for (const octet of octets) {
        const num = Number.parseInt(octet, 10)
        if (num < 0 || num > 255) {
          return false
        }
      }
    }

    return true
  } catch (error) {
    // If URL constructor throws an error, the URL is invalid
    return false
  }
}
